import React, { useState } from 'react';
import { PolicyDocument } from '../contexts/DocumentContext';
import { useBackgroundReports } from '../contexts/BackgroundReportContext';
import { useToast } from '../contexts/ToastContext';
import { jobsService } from '../services/jobsService';

interface Props {
  customerId: string;
  customerName: string;
  projectId: string;
  projectTitle: string;
  documents: PolicyDocument[];
  onClose: () => void;
  onQueued: () => void;
}

const InsuranceSummaryCreateModal: React.FC<Props> = ({
  customerId,
  customerName,
  projectId,
  projectTitle,
  documents,
  onClose,
  onQueued,
}) => {
  const { addBackgroundReport, refreshReports } = useBackgroundReports();
  const { showToast } = useToast();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(documents.map((d) => d.id))
  );
  const [title, setTitle] = useState<string>(`Insurance Summary — ${projectTitle}`);
  const [context, setContext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(documents.map((d) => d.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handleGenerate = async () => {
    if (selectedIds.size === 0) {
      setError('Select at least one document.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('riskninja-token') || '';
      const finalTitle = title.trim() || `Insurance Summary — ${projectTitle}`;
      const selectedDocs = documents.filter((d) => selectedIds.has(d.id));

      const response = await jobsService.submitJob(token, {
        workflowType: 'insurance_summary',
        customerId,
        projectId,
        documents: Array.from(selectedIds),
        reportTitle: finalTitle,
        context: context.trim() || undefined,
        additionalContext: context.trim() || undefined,
      });

      if (!response.success || !response.jobId) {
        throw new Error('Backend did not return a job id');
      }

      addBackgroundReport({
        workflowId: response.jobId,
        workflowType: 'insurance_summary',
        customerName,
        customerId,
        projectId,
        documents: selectedDocs.map((d) => ({ id: d.id, name: d.name || (d as any).fileName })),
      });

      refreshReports();

      showToast({
        type: 'info',
        title: 'Insurance Summary Queued',
        message: `${finalTitle} is processing in the background. You'll be notified when it's ready.`,
        duration: 5000,
      });

      onQueued();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to queue summary.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-secondary dark:text-dark-text">
              Create Insurance Summary
            </h2>
            <p className="text-sm text-accent dark:text-dark-muted">
              {customerName} · {projectTitle}
            </p>
          </div>
          {!submitting && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
              Summary title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-secondary dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-secondary dark:text-dark-text">
                Documents to include ({selectedIds.size} of {documents.length})
              </label>
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  onClick={selectAll}
                  disabled={submitting}
                  className="text-primary hover:underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={selectNone}
                  disabled={submitting}
                  className="text-accent dark:text-dark-muted hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
            {documents.length === 0 ? (
              <p className="text-sm text-accent dark:text-dark-muted py-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                No documents in this project folder.
              </p>
            ) : (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {documents.map((doc) => (
                  <label
                    key={doc.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      submitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(doc.id)}
                      onChange={() => toggle(doc.id)}
                      disabled={submitting}
                      className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-secondary dark:text-dark-text truncate">
                        {doc.name}
                      </div>
                      <div className="text-xs text-accent dark:text-dark-muted">
                        {doc.policyType || 'Unknown'} ·{' '}
                        {doc.size ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
              Additional context <span className="text-accent dark:text-dark-muted font-normal">(optional)</span>
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              disabled={submitting}
              rows={3}
              placeholder="Anything the AI should know — e.g. which docs are primary vs. supporting, prior-year notes, special instructions."
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-secondary dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-secondary dark:text-dark-text bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={submitting || selectedIds.size === 0}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Queuing…' : 'Generate Summary'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceSummaryCreateModal;
